import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, FileText, AlertTriangle, Download } from "lucide-react";
import { Toaster } from "sonner";
import SchedaFirmaPublic from "../components/SchedaFirmaPublic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMBLEM = "/ginius-emblem.png";

const CATEGORIE = ["Avvocato", "Perito", "Notaio", "Commercialista", "Consulente del lavoro", "Geometra", "Architetto", "Ingegnere", "Altro"];
const REGIMI = ["Regime Forfettario", "Contabilità semplificata", "Contabilità ordinaria"];

// Iter122k67 · API client con path base
const api = {
  get: (path) => axios.get(`${API}${path}`),
  post: (path, body) => axios.post(`${API}${path}`, body),
};

export default function SchedaProfessionistaPublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scheda, setScheda] = useState({});
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState("scheda");  // Iter122k67 · scheda | firma | done
  const [signResult, setSignResult] = useState(null);
  const [inlineError, setInlineError] = useState("");

  const refetch = () => axios.get(`${API}/credius-scheda/${token}`).then((r) => {
    setData(r.data);
    setScheda(r.data.scheda || {});
    if (r.data.firma?.signed_at) setStep("done");
    else if (r.data.gia_completata) setStep("firma");
    else setStep("scheda");
  });

  useEffect(() => {
    refetch()
      .catch((e) => setError(e.response?.data?.detail || "Link non valido o scaduto."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const setF = (k, v) => setScheda((s) => ({ ...s, [k]: v }));

  const inviaScheda = async () => {
    if (!scheda.nome_cognome_ragione_sociale || !scheda.codice_fiscale) {
      setInlineError("Compila almeno Nome/Ragione sociale e Codice fiscale.");
      return;
    }
    setInlineError("");
    setBusy(true);
    try {
      await axios.post(`${API}/credius-scheda/${token}`, scheda);
      await refetch();  // aggiorna step -> firma
    } catch (e) {
      setInlineError(e.response?.data?.detail || "Invio non riuscito. Riprova.");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-800" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md bg-white border-2 border-red-200 rounded-2xl p-6 text-center space-y-3">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto" />
          <h1 className="text-lg font-bold text-red-700">Link non valido</h1>
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  const inputCls = "w-full h-11 border-2 border-slate-300 rounded-lg px-3 disabled:bg-slate-50 disabled:text-slate-500 mt-1";
  const readOnly = step === "done";

  // Doc list per componente firma
  const firma_docs = [
    { tipo: "convenzione", label: "Atto di Convenzione", preview_url: `${API}/credius-scheda/${token}/anteprima/convenzione` },
    { tipo: "privacy", label: "Informativa Privacy", preview_url: `${API}/credius-scheda/${token}/anteprima/privacy` },
    { tipo: "consenso", label: "Consenso al trattamento dati", preview_url: `${API}/credius-scheda/${token}/anteprima/consenso` },
  ];

  return (
    <>
      <Toaster richColors position="top-center" />
      <div className="min-h-screen bg-slate-50 py-6 px-4" data-testid="scheda-public">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <img src={EMBLEM} alt="Ginius" className="w-10 h-10" />
            <div>
              <div className="text-xs font-bold text-indigo-800 uppercase tracking-wider">Credius · Convenzione</div>
              <h1 className="text-xl font-bold text-slate-800">Scheda acquisizione dati professionista convenzionato</h1>
            </div>
          </div>
          {data?.titolare?.ragione_sociale && (
            <div className="text-xs text-slate-500 mb-3">
              Richiesta da: <b>{data.titolare.ragione_sociale}</b>{data.titolare.email ? ` · ${data.titolare.email}` : ""}
            </div>
          )}

          {/* Iter122k67 · Banner "già firmato" */}
          {step === "done" && data?.firma && (
            <div data-testid="firma-done-banner" className="bg-emerald-50 border-2 border-emerald-300 text-emerald-900 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-6 h-6 text-emerald-700" />
                <div className="font-bold">Documenti firmati elettronicamente</div>
              </div>
              <div className="text-sm">
                Firma completata il <b>{new Date(data.firma.signed_at).toLocaleString("it-IT")}</b> a nome di <b>{data.firma.signer_name}</b>.
                La scheda è ora in sola lettura.
              </div>
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {(data.firma.docs || []).map((d) => (
                  <a key={d.file_id} data-testid={`firma-download-${d.tipo}`}
                    href={`${API}/credius-scheda/${token}/firma/pdf/${d.file_id}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-white border-2 border-emerald-200 rounded-lg px-3 py-2 text-sm font-bold text-emerald-800 hover:bg-emerald-50">
                    <Download className="w-4 h-4" /> {d.filename}
                  </a>
                ))}
                {data.firma.attestato_file_id && (
                  <a data-testid="firma-download-attestato"
                    href={`${API}/credius-scheda/${token}/firma/pdf/${data.firma.attestato_file_id}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-white border-2 border-indigo-200 rounded-lg px-3 py-2 text-sm font-bold text-indigo-800 hover:bg-indigo-50">
                    <Download className="w-4 h-4" /> Attestato di firma
                  </a>
                )}
              </div>
            </div>
          )}

          {step === "firma" && !readOnly && (
            <div className="bg-emerald-50 border-2 border-emerald-200 text-emerald-800 rounded-xl p-3 mb-4 text-sm">
              ✓ Scheda ricevuta correttamente. Puoi ora firmare i 3 documenti in fondo alla pagina.
            </div>
          )}

          {inlineError && (
            <div className="bg-red-50 border-2 border-red-200 text-red-800 rounded-xl p-3 mb-4 text-sm">
              {inlineError}
            </div>
          )}

          {/* SCHEDA · sempre visibile; disabilitata in step done */}
          <div className={"bg-white rounded-2xl border-2 border-slate-200 p-5 space-y-4 " + (readOnly ? "opacity-90" : "")}>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-bold text-slate-700 uppercase">Nome / Cognome / Ragione Sociale *</label>
                <input data-testid="scheda-p-nome" className={inputCls} disabled={readOnly} value={scheda.nome_cognome_ragione_sociale || ""}
                  onChange={(e) => setF("nome_cognome_ragione_sociale", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase">Codice Fiscale *</label>
                <input data-testid="scheda-p-cf" className={inputCls} disabled={readOnly} value={scheda.codice_fiscale || ""}
                  onChange={(e) => setF("codice_fiscale", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase">P. IVA</label>
                <input data-testid="scheda-p-piva" className={inputCls} disabled={readOnly} value={scheda.partita_iva || ""}
                  onChange={(e) => setF("partita_iva", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase">Categoria professionale</label>
                <select className={inputCls + " bg-white"} disabled={readOnly} value={scheda.categoria_professionale || ""}
                  onChange={(e) => setF("categoria_professionale", e.target.value)}>
                  <option value="">— Seleziona —</option>
                  {CATEGORIE.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase">Ordine di appartenenza</label>
                <input className={inputCls} disabled={readOnly} value={scheda.ordine_appartenenza || ""}
                  onChange={(e) => setF("ordine_appartenenza", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase">Recapiti telefonici</label>
                <input className={inputCls} disabled={readOnly} value={scheda.recapiti_telefonici || ""}
                  onChange={(e) => setF("recapiti_telefonici", e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-bold text-slate-700 uppercase">Email / PEC</label>
                <input className={inputCls} disabled={readOnly} value={scheda.recapiti_email_pec || ""}
                  onChange={(e) => setF("recapiti_email_pec", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase">Indirizzo e civico</label>
                <input className={inputCls} disabled={readOnly} value={scheda.indirizzo || ""}
                  onChange={(e) => setF("indirizzo", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase">CAP</label>
                <input className={inputCls} disabled={readOnly} value={scheda.cap || ""}
                  onChange={(e) => setF("cap", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase">Città</label>
                <input className={inputCls} disabled={readOnly} value={scheda.citta || ""}
                  onChange={(e) => setF("citta", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase">Sito internet</label>
                <input className={inputCls} disabled={readOnly} value={scheda.sito_internet || ""}
                  onChange={(e) => setF("sito_internet", e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-bold text-slate-700 uppercase">Sedi operative</label>
                <textarea rows={2} className={inputCls + " h-auto py-2"} disabled={readOnly} value={scheda.sedi_operative || ""}
                  onChange={(e) => setF("sedi_operative", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase">Regime fiscale</label>
                <select className={inputCls + " bg-white"} disabled={readOnly} value={scheda.regime_fiscale || ""}
                  onChange={(e) => setF("regime_fiscale", e.target.value)}>
                  <option value="">— Seleziona —</option>
                  {REGIMI.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-slate-700 uppercase">Codice SDI</label>
                <input className={inputCls} disabled={readOnly} value={scheda.codice_sdi || ""}
                  onChange={(e) => setF("codice_sdi", e.target.value)} />
              </div>
              <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-700 mt-2">
                <input type="checkbox" disabled={readOnly} checked={!!scheda.obbligo_fatturazione_elettronica}
                  onChange={(e) => setF("obbligo_fatturazione_elettronica", e.target.checked)} />
                Obbligo di fatturazione elettronica
              </label>
            </div>
            {step === "scheda" && (
              <>
                <button type="button" data-testid="scheda-submit" disabled={busy} onClick={inviaScheda}
                  className="w-full h-12 bg-indigo-900 hover:bg-indigo-950 disabled:opacity-50 text-white font-bold rounded-lg flex items-center justify-center gap-2">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  Invia scheda e prosegui con la firma
                </button>
                <p className="text-[11px] text-slate-500 text-center">
                  I dati inviati saranno utilizzati esclusivamente per la formalizzazione della convenzione Credius e
                  trattati nel rispetto del GDPR (UE) 2016/679.
                </p>
              </>
            )}
          </div>

          {/* FIRMA · step dedicato (dopo scheda inviata) */}
          {step === "firma" && (
            <div className="mt-6">
              <SchedaFirmaPublic token={token} api={api} docs={firma_docs}
                onSigned={(res) => { setSignResult(res); refetch(); }} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
